import React, { Component } from 'react';
import Swal from 'sweetalert2';
import {
    getScope,
    setScope,
    subscribeToScope,
    scopeChangeBlocked,
    loadExperiences,
    reconcileScope,
    experienceName,
    scopeIsMeaningful,
    isScopedRoute
} from '../utils/experienceScope';

// The working context, in the top bar: which tenant, and which experience
// inside it. Lives in chrome rather than on a form because it is a mode — it
// changes what every scoped screen is editing, and a mode you can navigate
// away from without noticing is a mode that gets you editing the wrong thing.
//
// Renders nothing at all until the tenant has two or more experiences — a
// single-experience club should never learn the concept.
class ExperienceScopeBar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            experiences: [],
            scope: getScope(),
            open: false
        };
        this.handleSelect = this.handleSelect.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
    }

    async componentDidMount() {
        this.unsubscribe = subscribeToScope(scope => this.setState({ scope }));
        document.addEventListener('mousedown', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleKeydown);
        const experiences = await loadExperiences();
        // Drop a stored scope whose experience is gone before it can be shown
        // as a real choice or written to.
        reconcileScope(experiences);
        this.setState({ experiences });
    }

    componentWillUnmount() {
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('mousedown', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
    }

    handleOutsideClick(event) {
        if (this.state.open && this.wrapRef && !this.wrapRef.contains(event.target)) {
            this.setState({ open: false });
        }
    }

    handleKeydown(event) {
        if (event.key === 'Escape' && this.state.open) this.setState({ open: false });
    }

    async handleSelect(id) {
        this.setState({ open: false });
        if (id === this.state.scope) return;

        // The screen gets to object before its data is swapped underneath it.
        if (scopeChangeBlocked()) {
            const target = experienceName(this.state.experiences, id);
            const result = await Swal.fire({
                title: 'Switch experience?',
                text: `You have unsaved changes on this screen. Switching to ${target} will discard them.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Discard and switch',
                cancelButtonText: 'Stay here'
            });
            if (!result.value) return;
        }
        setScope(id);
    }

    render() {
        const { experiences, scope, open } = this.state;
        if (!scopeIsMeaningful(experiences)) return null;

        const currentName = experienceName(experiences, scope);
        const isDefault = !scope;
        const appliesHere = isScopedRoute(window.location.pathname);

        // On a tenant-wide screen this is not a control, so it does not look
        // like one. It used to show the current experience by name with a
        // dropdown beside it, which on a screen that has its own team picker
        // reads as two selectors for the same thing — and the inert one was
        // the more prominent. Said plainly instead; the scope is changed from
        // the screens it actually changes. A fresh TopBar mounts per screen,
        // so this is re-decided on every navigation.
        if (!appliesHere) {
            return (
                <div className="exp-scope is-inactive">
                    <span className="exp-scope-label">Scope</span>
                    <span className="exp-scope-note">same for all experiences</span>
                </div>
            );
        }

        return (
            <div
                className={`exp-scope${isDefault ? '' : ' is-custom'}`}
                ref={el => { this.wrapRef = el; }}
            >
                <span className="exp-scope-label">Editing</span>

                <button
                    type="button"
                    className="exp-scope-button"
                    onClick={this.toggle}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    title={`Editing the ${currentName} experience`}
                >
                    <span className="exp-scope-name">{currentName}</span>
                    <span className="fa fa-angle-down" aria-hidden="true"/>
                </button>

                {open &&
                    <ul className="exp-scope-menu" role="listbox" aria-label="Choose an experience">
                        <li className="exp-scope-menu-head">Experience to edit</li>
                        {experiences.map(exp => {
                            const value = exp.isDefault ? '' : exp.id;
                            const selected = value === scope;
                            return (
                                <li key={exp.id}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={selected}
                                        className={selected ? 'is-selected' : ''}
                                        onClick={() => this.handleSelect(value)}
                                    >
                                        <span className="exp-scope-check">{selected ? '✓' : ''}</span>
                                        <span>
                                            {exp.name}
                                            {exp.isDefault && <em> the app&rsquo;s main config</em>}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                        <li className="exp-scope-menu-foot">
                            Offers, Settings, Home Buttons, Side Menu, Bottom Tabs, Carousel
                            and Radio all apply to the experience selected here.
                        </li>
                    </ul>
                }
            </div>
        );
    }

    toggle() {
        this.setState(prev => ({ open: !prev.open }));
    }
}

export default ExperienceScopeBar;
